import { ContextHolder } from "@frontegg/react";
import useAppStore from "../stores/appStore";
import {
  AUTHORIZE_TIMEOUT_MS,
  TENANT_SWITCH_TIMEOUT_MS,
  TOKEN_EXPIRY_BUFFER_MS,
  TOKEN_POLL_INTERVAL_MS
} from "./tokenRefresh.consts";

let requestAuthorize = null;
let refreshPromise = null;
let decodedToken = null;
let decodedPayload = null;

export const registerTokenRefresh = (authorizeFn) => {
  requestAuthorize = authorizeFn;
};

export const getLiveAccessToken = () => {
  try {
    return ContextHolder.default().getAccessToken();
  } catch {
    return null;
  }
};

const getTokenPayload = (token) => {
  if (token !== decodedToken) {
    decodedToken = token;
    try {
      decodedPayload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    } catch {
      decodedPayload = null;
    }
  }
  return decodedPayload;
};

export const getTokenTenantId = (token) => getTokenPayload(token)?.tenantId || null;

// A token without a readable expiry is assumed valid — the server stays the authority
// and the 401-retry layer still covers it.
export const isTokenValid = (token) => {
  if (!token) {
    return false;
  }
  const expirySeconds = getTokenPayload(token)?.exp;
  if (!expirySeconds) {
    return true;
  }
  return (expirySeconds * 1000) - TOKEN_EXPIRY_BUFFER_MS > Date.now();
};

// The server authorizes against the JWT's active tenant, so a token for the wrong
// tenant can't be fixed by a refresh — only by the pending tenant switch. Tokens whose
// tenant can't be read are treated as matching so an opaque token never blocks a retry.
const matchesExpectedTenant = (token, expectedTenantId) => {
  if (!token || !expectedTenantId) {
    return true;
  }
  const tokenTenantId = getTokenTenantId(token);
  return !tokenTenantId || tokenTenantId === expectedTenantId;
};

const getExpectedTenantId = () => {
  const { selectedAccountId, userData } = useAppStore.getState();
  return userData?.tenantIds?.includes(selectedAccountId) ? selectedAccountId : null;
};

// Requests fired while a tenant switch is in flight would carry the old tenant's
// token, so hold the refresh until the switch settles (or times out).
const waitForTenantSwitch = () => new Promise((resolve) => {
  if (!useAppStore.getState().switchingTenant) {
    resolve();
    return;
  }

  const timeoutId = setTimeout(() => {
    unsubscribe();
    resolve();
  }, TENANT_SWITCH_TIMEOUT_MS);

  const unsubscribe = useAppStore.subscribe((state) => {
    if (!state.switchingTenant) {
      clearTimeout(timeoutId);
      unsubscribe();
      resolve();
    }
  });
});

// Covers the window where a request fails before the switchingTenant flag is visible:
// poll Frontegg until it hands out a token for the expected tenant (or time out).
const waitForTenantToken = (expectedTenantId) => new Promise((resolve) => {
  const cleanup = () => {
    clearInterval(intervalId);
    clearTimeout(timeoutId);
  };

  const intervalId = setInterval(() => {
    const liveToken = getLiveAccessToken();
    if (matchesExpectedTenant(liveToken, expectedTenantId)) {
      cleanup();
      resolve(liveToken);
    }
  }, TOKEN_POLL_INTERVAL_MS);

  const timeoutId = setTimeout(() => {
    cleanup();
    resolve(getLiveAccessToken());
  }, TENANT_SWITCH_TIMEOUT_MS);
});

const resolveTenantToken = (expectedTenantId) => {
  const liveToken = getLiveAccessToken();
  if (!liveToken || matchesExpectedTenant(liveToken, expectedTenantId)) {
    return liveToken;
  }
  return waitForTenantToken(expectedTenantId);
};

// Bounded so a hung Frontegg call can't leave every outgoing request awaiting forever;
// authorize failures resolve too — callers proceed with the current token and the
// request fails naturally into the 401-retry layer.
const authorizeWithTimeout = () => {
  let timeoutId = null;
  return Promise.race([
    requestAuthorize().catch((error) => console.error("Frontegg authorize failed during token refresh:", error)),
    new Promise((resolve) => {
      timeoutId = setTimeout(resolve, AUTHORIZE_TIMEOUT_MS);
    })
  ]).finally(() => clearTimeout(timeoutId));
};

const performRefresh = async (failedToken) => {
  await waitForTenantSwitch();
  const expectedTenantId = getExpectedTenantId();

  const liveToken = await resolveTenantToken(expectedTenantId);
  if (liveToken && liveToken !== failedToken && matchesExpectedTenant(liveToken, expectedTenantId)) {
    useAppStore.getState().setAccessToken(liveToken);
    return liveToken;
  }

  if (!requestAuthorize) {
    return null;
  }

  await authorizeWithTimeout();
  const refreshedToken = await resolveTenantToken(expectedTenantId);
  if (refreshedToken) {
    useAppStore.getState().setAccessToken(refreshedToken);
  }
  return refreshedToken;
};

export const refreshAccessToken = ({ failedToken } = {}) => {
  if (!refreshPromise) {
    refreshPromise = performRefresh(failedToken).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// Pre-flight gate for outgoing requests: hands back the current token when it is
// usable, otherwise refreshes before the request leaves.
export const getValidAccessToken = async () => {
  const { accessToken, switchingTenant } = useAppStore.getState();
  const liveToken = getLiveAccessToken() || accessToken;
  const tokenIsUsable = !switchingTenant
    && isTokenValid(liveToken)
    && matchesExpectedTenant(liveToken, getExpectedTenantId());

  if (tokenIsUsable) {
    return liveToken;
  }

  try {
    const refreshedToken = await refreshAccessToken({ failedToken: liveToken });
    return refreshedToken || liveToken;
  } catch (error) {
    console.error("Failed to refresh access token before request:", error);
    return liveToken;
  }
};
